import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useForm, FormProvider, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import i18n, { common, input, organization } from "../../i18n";
import { useContext, useEffect, useState } from "react";
import { GoalSettingContext } from "../../contexts/GoalSettingContext";
import { CustomSelect2 } from "../../components/input/CustomSelect2";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { BaseYearSettingSection } from "./sections/BaseYearSettingSection";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { goalSettingService } from "../../service/goal-setting.service";
import { BaselineSettingSectionV2 } from "./sections/BaselineSettingSectionV2";
import alertService from "../../components/alert/alertService";
import { ApiError } from "../../api/ApiError";
import { isGoalCapRefusal } from "../../contexts/useGoalLimit";
import { GoalPlanBaseline } from "../../models/GoalSetting";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";

export const roundValue = (data: number, decimal: number = 2): number => {
  return parseFloat(data.toFixed(decimal));
};

export type GoalSettingFormValue = {
  type: "organization" | "suborganization";
  suborganizationRef: string;
  name: string;
  standard: string;
  methodology: string;
  scope: string;
  unit: string;
};

export const AddGoalSettingLayout = (): JSX.Element => {
  const { t } = useTranslation([common, input], { nsMode: "fallback" });
  const {
    // exitModal,
    setModalIsOpen,
    subOrganizations,
    setEmissionOptions,
    setMethodology,
    setIsPlanFetched,
    setDefaultBaseYear,
    isPlanFetched,
    goalInfo,
    isEditMode,
    setIsEditMode,
    setGoalInfo,
    setGoals,
    setRows,
    getGoalSettingList,
  } = GoalSettingContext();
  const OrgContextRef = useContext(OrganizationContext);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isGetSettingPlanActive, setIsGetSettingPlanActive] = useState(false);

  const form = useForm<GoalSettingFormValue>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      type: goalInfo?.type ?? "organization",
      suborganizationRef: goalInfo?.suborganizationRefId ?? "",
      name: goalInfo?.name ?? "",
      standard: goalInfo?.standard ?? "GHG Protocol",
      methodology: goalInfo?.methodology ?? "",
      scope: goalInfo?.scope ?? "",
      unit: goalInfo?.unit ?? "tCO2e",
    },
  });
  const { isDirty: mainDirty } = useFormState({ control: form.control });

  const exitModal = async () => {
    // ✅ ตรวจ reactive
    const baseYearDirty = window.__RHF_BASEYEAR__?.formState?.isDirty ?? false;
    const baselineDirty = window.__RHF_BASELINE__?.formState?.isDirty ?? false;

    const hasUnsavedChanges = mainDirty || baseYearDirty || baselineDirty;
    // console.log({ mainDirty, baseYearDirty, baselineDirty });

    if (!hasUnsavedChanges) {
      handleClose();
      return;
    }

    const isConfirmed = await alertService.selection(
      t("Unsaved-Changes"),
      t("Unsaved-Changes-Description"),
      t("Leave"),
      "#E5E7EB",
      true,
      "480px",
      t("Stay"),
      "var(--dark)",
    );
    if (isConfirmed) handleClose();
  };

  const handleClose = () => {
    form.reset();
    window.__RHF_BASEYEAR__?.reset?.();
    window.__RHF_BASELINE__?.reset?.();
    setModalIsOpen(false);
    setIsEditMode(false);
    setGoalInfo(null);
    setMethodology(null);
    setEmissionOptions([]);
    setIsPlanFetched(false);
  };

  useEffect(() => {
    const loadEditModeData = async () => {
      if (!isEditMode || !goalInfo) return;
      form.reset(undefined, { keepDirtyValues: true }); // reset other form state but keep defaultValues and form values

      // ✅ 1. reset form
      form.reset(
        {
          type: goalInfo.type,
          suborganizationRef: goalInfo.suborganizationRefId ?? "",
          name: goalInfo.name,
          standard: goalInfo.standard,
          methodology: goalInfo.methodology,
          scope: goalInfo.scope,
          unit: goalInfo.unit,
        },
        { keepDirtyValues: false },
      );

      // ✅ 2. update context state
      setMethodology(goalInfo.methodology);
      setIsPlanFetched(false); // ป้องกัน BaseYearSettingSection render ก่อน

      try {
        // ✅ 3. เตรียม payload เพื่อ fetch emission list ใหม่
        const payload: any = {
          name: goalInfo.name,
          type: goalInfo.type,
          organizationRef: OrgContextRef?.organization?._id,
          standard:
            goalInfo.standard === "GHG Protocol"
              ? "ghgProtocol"
              : goalInfo.standard,
          scope: goalInfo.scope,
          unit: goalInfo.unit,
        };

        if (goalInfo.type === "suborganization") {
          payload.suborganizationRef = goalInfo.suborganizationRefId;
        }

        // ✅ 4. fetch emission list
        const result = await goalSettingService.getEmissionByYear(payload);
        if (result?.emissions?.length) {
          setEmissionOptions(result.emissions);
          setIsPlanFetched(true);

          // ✅ 5. ตั้งค่า default base year (เพื่อผูกกับ section)
          const minYear = Math.min(...result.emissions.map((e) => e.year));
          setDefaultBaseYear(minYear);
        } else {
          setEmissionOptions([]);
          setIsPlanFetched(false);
        }
      } catch (error) {
        console.error("Error loading emission options in edit mode:", error);
        setEmissionOptions([]);
        setIsPlanFetched(false);
      }
    };

    loadEditModeData();
  }, [isEditMode]);

  const methodology = form.watch("methodology");
  const type = form.watch("type");
  const subOrg = form.watch("suborganizationRef");
  const scope = form.watch("scope");

  useEffect(() => {
    // ✅ รีเซ็ตเฉพาะเมื่อ user แก้ไขจริง ไม่ใช่ตอนโหลด edit mode
    if (
      form.formState.dirtyFields.methodology ||
      form.formState.dirtyFields.type ||
      form.formState.dirtyFields.scope ||
      form.formState.dirtyFields.suborganizationRef
    ) {
      // ล้างข้อมูล section และตาราง
      setEmissionOptions([]);
      setIsPlanFetched(false);
      setDefaultBaseYear(null);

      // reset ฟอร์ม section ย่อย
      window.__RHF_BASEYEAR__?.reset?.();
      window.__RHF_BASELINE__?.reset?.();

      // console.log("Section reset triggered by user changes");
    }
  }, [
    type,
    subOrg,
    scope,
    methodology,
    form.formState.dirtyFields.methodology,
    form.formState.dirtyFields.type,
    form.formState.dirtyFields.scope,
    form.formState.dirtyFields.suborganizationRef,
  ]);

  const onSubmit = async (formData: GoalSettingFormValue) => {
    try {
      setMethodology(formData.methodology);

      const payload: any = {
        type: formData.type,
        name: formData.name,
        organizationRef: OrgContextRef?.organization?._id,
        standard:
          formData.standard === "GHG Protocol"
            ? "ghgProtocol"
            : formData.standard,
        scope: formData.scope,
        unit: formData.unit,
      };

      if (formData.type === "suborganization") {
        payload.suborganizationRef = formData.suborganizationRef;
      }

      const result = await goalSettingService.getEmissionByYear(payload);

      if (result?.emissions) {
        setEmissionOptions(result.emissions);
        setIsPlanFetched(true);

        const minYear = Math.min(...result.emissions.map((e) => e.year));
        setDefaultBaseYear(minYear);
        alertService.mixin("Updated-Success");
      } else {
        alertService.mixin("No-Emission-Data");
        setEmissionOptions([]);
        setIsPlanFetched(false);
      }
    } catch (error) {
      alertService.mixin("Emission-Retrieve-Failed");
      setIsPlanFetched(false);
    }
  };

  // 🟢 SAVE HANDLER
  const handleSaveGoal = async () => {
    const isConfirmed = await alertService.confirm(
      t("Save-Changes"),
      t("Would-you-like-to-save-your-goal-settings"),
      t("Save"),
      "var(--dark)",
    );
    if (!isConfirmed) return;

    try {
      let response = null;
      const baseFormValues = form.getValues();
      const refId = goalInfo?.goalRefId ?? null;
      const orgRef = OrgContextRef?.organization?._id ?? "";

      // 🧩 แปลงค่า standard ก่อนสร้าง payload
      const normalizedStandard =
        baseFormValues.standard === "GHG Protocol"
          ? "ghgProtocol"
          : baseFormValues.standard;

      // 🔹 BaseYear Mode
      if (methodology === "baseYear") {
        const section = window.__RHF_BASEYEAR__?.getValues?.() ?? {};
        const { methodology, ...baseValues } = baseFormValues;
        const { rows, startYear, targetYear, ...filtered } = section;
        // console.log("section", section)
        const validPlans = (rows || []).filter(
          (p: any) =>
            p.year >= (startYear ?? 0) && p.year <= (targetYear ?? 9999),
        );

        const formattedPlans = validPlans.map((p: any) => ({
          ...p,
          baseYearEmission: Number(p.baseYearEmission ?? 0),
          percentReduction: Number(p.percentReduction ?? 0),
          targetReduction: Number(p.targetReduction ?? 0),
          targetEmission: Number(p.targetEmission ?? 0),
        }));

        const payload: any = {
          organizationRef: orgRef,
          ...baseValues,
          ...filtered,
          startYear: startYear,
          targetYear: targetYear,
          standard: normalizedStandard, // ✅ ใช้ค่าแปลงแล้ว
          plans: formattedPlans,
        };

        // ❌ หาก type เป็น organization → ตัด suborganizationRef ออก
        if (payload.type === "organization") {
          delete payload.suborganizationRef;
        }

        if (isEditMode && refId) {
          response = await goalSettingService.updatedGoalBaseYear(refId, {
            methodology: "baseYear",
            plan: payload,
          });
        } else {
          response = await goalSettingService.createGoalBaseYear(payload);
        }
      }

      // 🔹 Baseline Mode
      else if (methodology === "baseline") {
        const section = window.__RHF_BASELINE__?.getValues?.() ?? {};
        const { methodology, ...baseValues } = baseFormValues;
        const { rows, startYear, targetYear, ...filtered } = section;

        // console.log("section",section)

        const validPlans = (rows || []).filter(
          (p: any) =>
            p.year >= (startYear ?? 0) && p.year <= (targetYear ?? 9999),
        );

        const formattedPlans = validPlans.map((p: any) => ({
          ...p,
          bauEmission: Number(p.bauEmission ?? 0),
          percentReduction: Number(p.percentReduction ?? 0),
          percentAnnualGrowthRate: Number(p.percentAnnualGrowthRate ?? 0),
          targetReduction: Number(p.targetReduction ?? 0),
          targetEmission: Number(p.targetEmission ?? 0),
        }));

        const payload: any = {
          organizationRef: orgRef,
          ...filtered,
          ...baseValues,
          startYear: startYear,
          targetYear: targetYear,
          standard: normalizedStandard, // ✅ ใช้ค่าแปลงแล้ว
          plans: formattedPlans,
        };

        if (payload.type === "organization") {
          delete payload.suborganizationRef;
        }

        if (isEditMode && refId) {
          // console.log("edit mode", payload)
          response = await goalSettingService.updatedGoalBaseline(refId, {
            methodology: "baseline",
            plan: payload,
          });
        } else {
          response = await goalSettingService.createGoalBaseline(payload);
        }
      }

      // ✅ หลังจากสำเร็จ
      if (response?.goals) {
        setGoals(response.goals);
        setRows(response.goals);
        setModalIsOpen(false);
        alertService.success(t("Goal-Update-Success"));
      }
    } catch (error) {
      console.error(error);
      if (!(error instanceof ApiError)) {
        alertService.error(t("Save-Failed"));
      }
      // the list is stale; reloading it shows the lock and closes this form
      if (isGoalCapRefusal(error)) getGoalSettingList();
    }
  };

  const handleReset = async () => {
    // console.log(goalInfo);
    if (!isEditMode || !goalInfo) return;

    const isConfirmed = await alertService.confirm(
      t("Reset"),
      t("Reset-Original-Values"),
      t("Reset"),
    );
    if (!isConfirmed) return;

    // ✅ reset ฟอร์มหลัก
    form.reset({
      type: goalInfo.type,
      suborganizationRef: goalInfo.suborganizationRefId ?? "",
      name: goalInfo.name,
      standard: goalInfo.standard,
      methodology: goalInfo.methodology,
      scope: goalInfo.scope,
      unit: goalInfo.unit,
    });

    // ✅ reset เฉพาะ section ตาม methodology
    if (goalInfo.methodology === "baseYear") {
      setMethodology("baseYear");
      const baseYearPayload = {
        baseYear: goalInfo.baseYear ?? null,
        startYear: goalInfo.startYear ?? null,
        targetYear: goalInfo.targetYear ?? null,
        baseYearEmission: goalInfo.baseYearEmission ?? 0,
        goalNetEmission: goalInfo.goalNetEmission ?? 0,
        percentReduction: goalInfo.percentReduction ?? 0,
        targetAbsoluteRate: goalInfo.targetAbsoluteRate ?? true,
        rows: goalInfo.plans ?? [], // ✅ map plans → rows
      };
      window.__RHF_BASEYEAR__?.reset?.(baseYearPayload);
    }

    if (goalInfo.methodology === "baseline") {
      setMethodology("baseline");

      const baselinePayload = {
        baseYear: goalInfo.baseYear ?? null,
        startYear: goalInfo.startYear ?? null,
        targetYear: goalInfo.targetYear ?? null,
        bauEmission: goalInfo.bauEmission ?? 0,
        goalNetEmission: goalInfo.goalNetEmission ?? 0,
        percentReduction: goalInfo.percentReduction ?? 0,
        percentAnnualGrowthRate: goalInfo.percentAnnualGrowthRate ?? 0,
        targetAbsoluteRate: goalInfo.targetAbsoluteRate ?? true,
        startAbsoluteRate: goalInfo.startAbsoluteRate ?? true,
        rows: (goalInfo.plans as GoalPlanBaseline[]) ?? [],
      };

      window.__RHF_BASELINE__?.reset?.(baselinePayload);
    }

    setTimeout(() => {
      alertService.mixin(t("Reset-to-Original-Values"));
    }, 200);
  };

  const isGetSettingPlanDisabled =
    isPlanFetched ||
    !form.formState.isValid ||
    !(
      form.formState.dirtyFields.methodology ||
      form.formState.dirtyFields.type ||
      form.formState.dirtyFields.scope ||
      form.formState.dirtyFields.suborganizationRef
    );

  // 🟢 เฝ้าดูการเปลี่ยนแปลง แล้วตั้งค่า state ตามจริง
  useEffect(() => {
    setIsGetSettingPlanActive(!isGetSettingPlanDisabled);
  }, [isGetSettingPlanDisabled]);

  useEffect(() => {
    const checkBaseYearRows = () => {
      const sectionVisible = !!document.querySelector(
        "#Actived-BaseYearSection",
      );
      if (!sectionVisible) {
        setIsSaveDisabled(true);
        return;
      }

      const rows = window.__RHF_BASEYEAR__?.getValues?.()?.rows ?? [];
      const isValidArray = Array.isArray(rows);
      const hasRows = isValidArray && rows.length > 0;
      setIsSaveDisabled(!hasRows);

      // console.log("🔍 [checkBaseYearRows]", {
      //   sectionVisible,
      //   rowCount: rows?.length ?? 0,
      //   firstRowSample: isValidArray ? rows.slice(0, 2) : [],
      //   isSaveDisabled: !hasRows,
      //   baseYearFormExists: !!window.__RHF_BASEYEAR__,
      // });
    };

    const checkBaselineRows = () => {
      const sectionVisible = !!document.querySelector(
        "#Actived-BaselineSection",
      );
      if (!sectionVisible) {
        setIsSaveDisabled(true);
        return;
      }

      const rows = window.__RHF_BASELINE__?.getValues?.()?.rows ?? [];
      const isValidArray = Array.isArray(rows);
      const hasRows = isValidArray && rows.length > 0;
      setIsSaveDisabled(!hasRows);

      // console.log("🔍 [checkBaselineRows]", {
      //   sectionVisible,
      //   rowCount: rows?.length ?? 0,
      //   firstRowSample: isValidArray ? rows.slice(0, 2) : [],
      //   isSaveDisabled: !hasRows,
      //   baselineFormExists: !!window.__RHF_BASELINE__,
      // });
    };

    const interval = setInterval(() => {
      if (methodology === "baseYear") checkBaseYearRows();
      else if (methodology === "baseline") checkBaselineRows();
    }, 100);

    return () => clearInterval(interval);
  }, [methodology]);

  return (
    <FormProvider {...form}>
      <Box paddingX={"20px"}>
        <Typography
          variant="h1"
          color={"var(--dark)"}
          sx={{ marginBottom: "10px" }}
        >
          <IconButton sx={{ marginLeft: "-8px" }} onClick={exitModal}>
            <img src="/img/arrow-left.svg" alt="" />
          </IconButton>
          {isEditMode ? t("Goal-Details") : t("Add-New-Goal")}
        </Typography>
        {isEditMode ? (
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            bgcolor="var(--gray3)"
            p="12px 16px"
            borderRadius="8px"
            width="100%"
            boxSizing="border-box"
            alignSelf="stretch"
          >
            <Box display={"flex"} alignItems="center" gap={1} flex={1}>
              <InfoOutlinedIcon color="primary" />
              <Typography variant="h4" color={"primary"}>
                {t("Note")}:
              </Typography>
              <Typography variant="body1" color={"primary"}>
                {t("Goal-Created-By")} {goalInfo?.createdByName} ,{" "}
                {goalInfo?.createdAt
                  ? dayjs(goalInfo.createdAt)
                      .locale(i18n.language) // เปลี่ยนภาษาอัตโนมัติ
                      .format("DD MMM YYYY")
                  : ""}
              </Typography>
            </Box>
            <Box display={"flex"} alignItems="center" gap={1} flex={1}>
              <Typography variant="h4" color={"primary"}>
                {t("Note")}:
              </Typography>
              <Typography variant="body1" color={"primary"}>
                {t("Goal-Updated-By")} {goalInfo?.updatedByName} ,{" "}
                {goalInfo?.createdAt
                  ? dayjs(goalInfo.updatedAt)
                      .locale(i18n.language) // เปลี่ยนภาษาอัตโนมัติ
                      .format("DD MMM YYYY")
                  : ""}
              </Typography>
            </Box>
          </Box>
        ) : (
          <></>
        )}

        <Card variant="outlined" sx={{ p: "20px", mt: 2 }}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid2 container spacing={"20px"} disableEqualOverflow>
              <Grid2 xs={12} md={6}>
                <CustomSelect2
                  name="type"
                  info={""}
                  placeholder={""}
                  control={form.control}
                  label={t("Type")}
                  listMenuItem={[
                    {
                      nameEN: t("Organization"),
                      nameTH: t("Organization"),
                      value: "organization",
                    },
                    {
                      nameEN: t("Sub-Organizations"),
                      nameTH: t("Sub-Organizations"),
                      value: "suborganization",
                    },
                  ]}
                  required
                  defaultValue={"organization"}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                {form.watch("type") === "suborganization" && (
                  <CustomSelect2
                    name="suborganizationRef"
                    info={""}
                    placeholder={""}
                    control={form.control}
                    label={t("Sub-Organizations")}
                    listMenuItem={subOrganizations.map((s) => ({
                      nameEN: s.suborganizationName,
                      nameTH: s.suborganizationName,
                      value: s.suborganizationRefId,
                      disabled: !s.hasEmission,
                    }))}
                    required
                    rules={{
                      required: t("This-field-is-required", {
                        param: t("Sub-Organizations"),
                      }),
                    }}
                  />
                )}
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  name="name"
                  control={form.control}
                  label={t("Goal-Name")}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Goal-Name"),
                    }),
                  }}
                  placeholder={t("Goal-Name")}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  name="standard"
                  control={form.control}
                  label={t("Standard")}
                  info={t("Standard-Description")}
                  required
                  defaultValue={"GHG Protocol"}
                  displayValue="GHG Protocol"
                  placeholder=""
                  disabled
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <CustomSelect2
                  name="methodology"
                  info={""}
                  placeholder={""}
                  control={form.control}
                  label={t("Methodology")}
                  listMenuItem={[
                    {
                      nameEN: t("Base-Year"),
                      nameTH: t("Base-Year"),
                      value: "baseYear",
                    },
                    {
                      nameEN: t("Base-Line"),
                      nameTH: t("Base-Line"),
                      value: "baseline",
                    },
                  ]}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Methodology"),
                    }),
                  }}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <CustomSelect2
                  name="scope"
                  info={""}
                  placeholder={""}
                  control={form.control}
                  label={t("Scope")}
                  listMenuItem={[
                    {
                      nameEN: t("Scope1-2"),
                      nameTH: t("Scope1-2"),
                      value: "scope12",
                    },
                    {
                      nameEN: t("Scope1-3"),
                      nameTH: t("Scope1-3"),
                      value: "scope123",
                    },
                  ]}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Scope"),
                    }),
                  }}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <CustomSelect2
                  name="unit"
                  info={""}
                  disabled
                  placeholder={""}
                  control={form.control}
                  label={t("Unit")}
                  listMenuItem={[
                    {
                      nameEN: `${t("tCO₂e")} (Default)`,
                      nameTH: `${t("tCO₂e")} (ค่าเริ่มต้น)`,
                      value: "tCO2e",
                    },
                  ]}
                  required
                />
              </Grid2>

              <Grid2
                xs={12}
                md={6}
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-end"
                mb={1}
              >
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{ maxHeight: "37.13px" }}
                  onClick={() => setIsGetSettingPlanActive(true)}
                  disabled={isGetSettingPlanDisabled}
                >
                  {t("Get-Setting-Plan")}
                </Button>
              </Grid2>
            </Grid2>
          </form>

          <Divider sx={{ my: 2 }} />

          {methodology === "baseYear" && (
            <BaseYearSettingSection id="Actived-BaseYearSection" />
          )}
          {/* {methodology === "baseline" && <BaselineSettingSection />} */}
          {methodology === "baseline" && (
            <BaselineSettingSectionV2 id="Actived-BaselineSection" />
          )}
        </Card>
        <Box mt={4} display="flex" justifyContent="space-between">
          <Button variant="outlined" color="secondary" onClick={exitModal}>
            {t("Back")}
          </Button>
          <Box display="flex" gap={2}>
            {isEditMode && (
              <Button
                variant="outlined"
                color="secondary"
                disabled={isGetSettingPlanActive}
                onClick={handleReset}
              >
                {t("Reset")}
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              disabled={isSaveDisabled}
              onClick={handleSaveGoal}
            >
              {t("Save")}
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};
